import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { buildFeedbackTitle, capturePageContext, captureScreenshotFile } from 'src/components/feedback/capture-page-context';
import {
  FeedbackDsButton,
  FeedbackDsModal,
} from 'src/components/feedback/feedback-design-system/feedback-ds-modal';
import {
  FeedbackDsField,
  FeedbackDsFileUpload,
  FeedbackDsInput,
  FeedbackDsSegmentedControl,
  FeedbackDsTextarea,
} from 'src/components/feedback/feedback-design-system/feedback-ds-form-fields';
import { feedbackDs } from 'src/components/feedback/feedback-design-system/feedback-ds-classes';
import { useSubmitFeedbackMutation } from 'src/redux/feedback/feedback.endpoints';
import {
  FeedbackSubmissionError,
  FeedbackType,
  PageContext,
} from 'src/redux/feedback/feedback.types';
import { showNotification } from 'src/redux/notification/notification.slice';
import ErrorLogService from 'src/services/error-log/error-log.service';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: 'Bug', label: 'Bug' },
  { value: 'Improvement', label: 'Improvement' },
  { value: 'Feature', label: 'Feature' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoScreenshotPromiseRef = useRef<Promise<File | undefined> | null>(null);

  const [type, setType] = useState<FeedbackType>('Bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    type?: string;
    title?: string;
    description?: string;
    pageContext?: string;
  }>({});

  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();

  useEffect(() => {
    if (isOpen) {
      setPageContext(capturePageContext());
      setTitle((currentTitle) => currentTitle.trim() ? currentTitle : buildFeedbackTitle());
      autoScreenshotPromiseRef.current = captureScreenshotFile();
      return;
    }

    setType('Bug');
    setTitle('');
    setDescription('');
    setScreenshotFile(null);
    setPageContext(null);
    setValidationErrors({});
    autoScreenshotPromiseRef.current = null;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const errors: {
      type?: string;
      title?: string;
      description?: string;
    } = {};

    if (!title.trim()) {
      errors.title = 'Title is required';
    }

    if (!description.trim()) {
      errors.description = 'Description is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const applyServerFieldErrors = (fieldErrors: FeedbackSubmissionError['fieldErrors']) => {
    if (!fieldErrors) {
      return;
    }

    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      ...fieldErrors,
    }));
  };

  const handleScreenshotChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setScreenshotFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      dispatch(showNotification({
        summary: 'Please upload a valid image file',
        sticky: false,
        theme: 'warning',
      }));
      event.target.value = '';
      setScreenshotFile(null);
      return;
    }

    setScreenshotFile(file);
  };

  const handleSubmit = async() => {
    if (!validateForm() || !pageContext) {
      return;
    }

    try {
      const autoScreenshot = screenshotFile ? undefined : await autoScreenshotPromiseRef.current ?? undefined;
      const response = await submitFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        pageContext,
        screenshot: screenshotFile ?? autoScreenshot,
      }).unwrap();

      dispatch(showNotification({
        summary: 'Feedback submitted successfully',
        sticky: false,
        theme: 'success',
      }));

      if (response.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      }

      onClose();
    } catch (error) {
      const submissionError = error as FeedbackSubmissionError;
      ErrorLogService.logError(error ?? 'Feedback submission error', 'feedback-modal', 'handleSubmit');

      applyServerFieldErrors(submissionError.fieldErrors);

      dispatch(showNotification({
        summary: submissionError.message ?? 'Failed to submit feedback. Please try again.',
        sticky: submissionError.isRetryable ?? false,
        theme: 'warning',
      }));
    }
  };

  return (
    <FeedbackDsModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title="Submit feedback"
      description="Share bugs, improvements, or feature requests with the team."
      footer={(
        <>
          <FeedbackDsButton
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </FeedbackDsButton>
          <FeedbackDsButton
            variant="accent"
            onClick={() => void handleSubmit()}
            disabled={isLoading}
          >
            {isLoading ? 'Submitting…' : 'Submit Feedback'}
          </FeedbackDsButton>
        </>
      )}
    >
      <div className={feedbackDs.callout}>
        Page context is captured automatically so the team can reproduce the issue.
      </div>

      <FeedbackDsField
        label="Type"
        error={validationErrors.type}
      >
        <FeedbackDsSegmentedControl
          name="Feedback type"
          options={FEEDBACK_TYPE_OPTIONS}
          value={type}
          onChange={setType}
        />
      </FeedbackDsField>

      <FeedbackDsField
        label="Title"
        htmlFor="feedback-title"
        required
        error={validationErrors.title}
      >
        <FeedbackDsInput
          id="feedback-title"
          placeholder="Brief summary of your feedback"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          error={Boolean(validationErrors.title)}
        />
      </FeedbackDsField>

      <FeedbackDsField
        label="Description"
        htmlFor="feedback-description"
        required
        error={validationErrors.description}
        hint="Include steps to reproduce, expected behaviour, and what actually happened."
      >
        <FeedbackDsTextarea
          id="feedback-description"
          placeholder="Describe the issue, improvement, or feature request in detail..."
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={6}
          error={Boolean(validationErrors.description)}
        />
      </FeedbackDsField>

      <FeedbackDsField
        label="Screenshot"
        htmlFor="feedback-screenshot-input"
      >
        <FeedbackDsFileUpload
          id="feedback-screenshot-input"
          inputRef={fileInputRef}
          fileName={screenshotFile?.name ?? null}
          onChange={handleScreenshotChange}
          disabled={isLoading}
        />
      </FeedbackDsField>
    </FeedbackDsModal>
  );
};

export default FeedbackModal;
